import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  VStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react'
import { renderAsync } from 'docx-preview'
import { FileItem } from '@/types'
import apiService from '@/services/api'

interface DocxViewerProps {
  file: FileItem
  scale: number
  onScaleChange: (scale: number) => void
}

export default function DocxViewer({ file, scale }: DocxViewerProps) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const loadDocument = useCallback(async () => {
    const container = containerRef.current
    if (!container) return

    setIsLoading(true)
    setError(null)
    container.innerHTML = ''

    try {
      const url = apiService.getFilePreviewUrl(file.id)
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch file')
      const arrayBuffer = await response.arrayBuffer()

      await renderAsync(arrayBuffer, container, undefined, {
        className: 'docx-preview',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
      })

      setIsLoading(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load document')
      setIsLoading(false)
    }
  }, [file.id])

  useEffect(() => {
    void loadDocument()
  }, [loadDocument])

  if (error && !isLoading) {
    return (
      <Center h="full">
        <Alert status="error" maxW="md" borderRadius="lg">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="semibold">Failed to load document</Text>
            <Text fontSize="sm">{error}</Text>
            <Button size="sm" onClick={() => void loadDocument()}>
              Retry
            </Button>
          </VStack>
        </Alert>
      </Center>
    )
  }

  return (
    <Box h="full" display="flex" flexDirection="column">
      <Box
        ref={wrapperRef}
        flex={1}
        overflow="auto"
        display="flex"
        justifyContent="center"
        alignItems="flex-start"
        bg="gray.100"
        p={4}
        className="ph-no-capture file-content-private"
        data-private="true"
      >
        {isLoading && (
          <Center position="absolute" inset={0} zIndex={1}>
            <VStack spacing={4}>
              <Spinner size="xl" color="brand.500" />
              <Text color="gray.500">Loading document...</Text>
            </VStack>
          </Center>
        )}

        <Box
          ref={containerRef}
          transform={`scale(${scale})`}
          transformOrigin="top center"
          sx={{
            '.docx-wrapper': {
              bg: 'white',
              boxShadow: 'lg',
              borderRadius: 'md',
              minHeight: '500px',
              padding: '0 !important',
              background: 'white !important',
            },
            '.docx-wrapper > section.docx': {
              boxShadow: 'md',
              marginBottom: '16px',
              padding: '96px 72px',
              bg: 'white',
            },
          }}
        />
      </Box>
    </Box>
  )
}
